import { useEffect, useMemo, useState } from 'react';
import { Plus, Save, Trash2, X } from 'lucide-react';
import { fmtMoney } from '../lib/format';
import type { CashflowBankAccount, CashflowTermDeposit } from '../cashflowTypes';

type BankRole = CashflowBankAccount['role'];

const BANK_ROLES: BankRole[] = ['Operating', 'Payroll', 'Multi Currency'];

/** Format an ISO timestamp as YYYY-MM-DD in local time, suitable for
 *  an <input type="date"> value. */
function isoToDateInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (!isFinite(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Parse a date input value (YYYY-MM-DD, treated as local midnight) and
 *  return an ISO UTC timestamp for storage. */
function dateInputToIso(local: string): string | null {
  if (!local) return null;
  const d = new Date(local + 'T00:00');
  if (!isFinite(d.getTime())) return null;
  return d.toISOString();
}

const ROLE_HINTS: Record<BankRole | 'Term Deposit', string> = {
  Operating: 'Day-to-day cash. Source of opening operating balance.',
  Payroll: 'Ring-fenced — never drained by the drawdown waterfall.',
  'Multi Currency': 'Available to drain in the waterfall when operating goes negative.',
  'Term Deposit':
    'Drains on maturity (auto-rollover or to-account). Configure rollover policy below.',
};

type Props = {
  bankAccounts: CashflowBankAccount[];
  termDeposits: CashflowTermDeposit[];
  onSave: (next: {
    bankAccounts: CashflowBankAccount[];
    termDeposits: CashflowTermDeposit[];
  }) => Promise<void>;
  onClose: () => void;
  isViewer?: boolean;
};

/** Unified Bank Accounts panel.
 *
 *  Replaces the old "Update balances" + "Term Deposits" pair of buttons.
 *  Lists every bank account grouped by role (Operating, Payroll, Multi
 *  Currency) and the Term Deposit register as a fourth group. Full CRUD:
 *  add / edit / delete from one place; one Save button persists both
 *  arrays via saveCashflowData. */
export function CashflowBankPanel({
  bankAccounts,
  termDeposits,
  onSave,
  onClose,
  isViewer,
}: Props) {
  const [accounts, setAccounts] = useState<CashflowBankAccount[]>(() =>
    bankAccounts.map((a) => ({ ...a })),
  );
  const [tds, setTds] = useState<CashflowTermDeposit[]>(() => termDeposits.map((t) => ({ ...t })));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const totalsByRole = useMemo(() => {
    const t: Record<string, number> = {
      Operating: 0,
      Payroll: 0,
      'Term Deposit': 0,
      'Multi Currency': 0,
    };
    accounts.forEach((a) => {
      t[a.role] = (t[a.role] ?? 0) + (Number(a.currentBalance) || 0);
    });
    tds.forEach((td) => {
      (t['Term Deposit'] as number) += Number(td.principal) || 0;
    });
    return t;
  }, [accounts, tds]);

  // ── account mutations ────────────────────────────────────────────────────
  const updateAccount = (id: string, patch: Partial<CashflowBankAccount>) => {
    setAccounts((prev) => prev.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  };

  const addAccount = (role: BankRole) => {
    const maxOrder = accounts.reduce((m, a) => Math.max(m, a.displayOrder ?? 0), 0);
    setAccounts((prev) => [
      ...prev,
      {
        id: `acc_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        name: '',
        xeroAccountCode: null,
        role,
        currentBalance: 0,
        balanceAsAt: new Date().toISOString(),
        displayOrder: maxOrder + 10,
      },
    ]);
  };

  const removeAccount = (id: string) => {
    setAccounts((prev) => prev.filter((a) => a.id !== id));
  };

  // ── TD mutations ─────────────────────────────────────────────────────────
  const updateTd = (id: string, patch: Partial<CashflowTermDeposit>) => {
    setTds((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  };

  const addTd = () => {
    const today = new Date().toISOString().slice(0, 10);
    const oneYearOut = (() => {
      const d = new Date();
      d.setFullYear(d.getFullYear() + 1);
      return d.toISOString().slice(0, 10);
    })();
    const maxOrder = tds.reduce((m, t) => Math.max(m, t.displayOrder ?? 0), 0);
    setTds((prev) => [
      ...prev,
      {
        id: `td_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        bank: '',
        reference: '',
        principal: 0,
        rateAnnual: 0,
        startDate: today,
        maturityDate: oneYearOut,
        rolloverPolicy: 'auto-rollover',
        destinationAccountId: null,
        displayOrder: maxOrder + 10,
      },
    ]);
  };

  const removeTd = (id: string) => {
    setTds((prev) => prev.filter((t) => t.id !== id));
  };

  // ── save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (isViewer) return;
    setBusy(true);
    setErr(null);
    try {
      await onSave({ bankAccounts: accounts, termDeposits: tds });
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- backdrop mousedown-outside-to-close duplicates the explicit Close button inside
    <div
      className="fixed inset-0 bg-black/40 flex items-start justify-center pt-12 px-4 z-30"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="panel w-full max-w-5xl bg-white shadow-lg max-h-[90vh] flex flex-col">
        <div className="panel-section flex items-center justify-between border-b border-edge shrink-0">
          <div className="panel-section-title">Bank accounts</div>
          <button
            type="button"
            onClick={onClose}
            className="btn btn-ghost btn-sm"
            aria-label="Close"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="panel-section overflow-auto grow space-y-6">
          {BANK_ROLES.map((role) => {
            const rows = accounts
              .filter((a) => a.role === role)
              .sort((a, b) => a.displayOrder - b.displayOrder);
            return (
              <section key={role}>
                <div className="flex items-baseline justify-between mb-1">
                  <h3 className="text-[13px] font-semibold text-ccaanz-black">{role}</h3>
                  <span className="text-[11px] text-ccaanz-grey num">
                    {fmtMoney(totalsByRole[role] ?? 0)}
                  </span>
                </div>
                <div className="text-[11px] text-ccaanz-grey mb-2">{ROLE_HINTS[role]}</div>

                {rows.length === 0 && (
                  <div className="text-[12px] text-ccaanz-grey italic py-1">
                    No {role} accounts.
                  </div>
                )}

                {rows.length > 0 && (
                  <table className="w-full text-[12px]">
                    <thead>
                      <tr className="text-ccaanz-grey border-b border-edge">
                        <th className="text-left py-1.5 pr-2 font-medium">Name</th>
                        <th className="text-left py-1.5 pr-2 font-medium">Xero code</th>
                        <th className="text-right py-1.5 pr-2 font-medium num">Current balance</th>
                        <th className="text-left py-1.5 pr-2 font-medium">As at</th>
                        <th className="py-1.5 font-medium w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((a) => (
                        <tr key={a.id} className="border-b border-edge/50">
                          <td className="py-1 pr-2">
                            <input
                              type="text"
                              className="input w-full"
                              placeholder="ASB Operating"
                              value={a.name}
                              disabled={isViewer}
                              onChange={(e) => updateAccount(a.id, { name: e.target.value })}
                            />
                          </td>
                          <td className="py-1 pr-2">
                            <input
                              type="text"
                              className="input w-24"
                              placeholder="(optional)"
                              value={a.xeroAccountCode ?? ''}
                              disabled={isViewer}
                              onChange={(e) =>
                                updateAccount(a.id, {
                                  xeroAccountCode: e.target.value || null,
                                })
                              }
                            />
                          </td>
                          <td className="py-1 pr-2">
                            <input
                              type="number"
                              step="0.01"
                              className="input num w-32 text-right font-medium text-ccaanz-blue"
                              value={a.currentBalance}
                              disabled={isViewer}
                              onChange={(e) =>
                                updateAccount(a.id, {
                                  currentBalance: parseFloat(e.target.value) || 0,
                                })
                              }
                            />
                          </td>
                          <td className="py-1 pr-2">
                            <input
                              type="date"
                              className="input"
                              value={isoToDateInput(a.balanceAsAt)}
                              disabled={isViewer}
                              onChange={(e) =>
                                updateAccount(a.id, {
                                  balanceAsAt: dateInputToIso(e.target.value),
                                })
                              }
                            />
                          </td>
                          <td className="py-1">
                            {!isViewer && (
                              <button
                                type="button"
                                onClick={() => removeAccount(a.id)}
                                className="text-ccaanz-grey hover:text-ccaanz-red"
                                title={`Delete ${a.name || 'this account'}`}
                                aria-label="Delete account"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {!isViewer && (
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={() => addAccount(role)}
                      className="btn btn-ghost btn-sm"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Add {role}
                    </button>
                  </div>
                )}
              </section>
            );
          })}

          {/* ── Term Deposit section ─────────────────────────────────────── */}
          <section>
            <div className="flex items-baseline justify-between mb-1">
              <h3 className="text-[13px] font-semibold text-ccaanz-black">Term Deposit</h3>
              <span className="text-[11px] text-ccaanz-grey num">
                {fmtMoney(totalsByRole['Term Deposit'] ?? 0)}
                <span className="ml-2">
                  {tds.length} TD{tds.length === 1 ? '' : 's'}
                </span>
              </span>
            </div>
            <div className="text-[11px] text-ccaanz-grey mb-2">{ROLE_HINTS['Term Deposit']}</div>

            {tds.length === 0 && (
              <div className="text-[12px] text-ccaanz-grey italic py-1">
                No Term Deposits recorded.
              </div>
            )}

            {tds.length > 0 && (
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="text-ccaanz-grey border-b border-edge">
                    <th className="text-left py-1.5 pr-2 font-medium">Bank</th>
                    <th className="text-left py-1.5 pr-2 font-medium">Reference</th>
                    <th className="text-right py-1.5 pr-2 font-medium num">Principal</th>
                    <th className="text-right py-1.5 pr-2 font-medium num">Rate %</th>
                    <th className="text-left py-1.5 pr-2 font-medium">Start</th>
                    <th className="text-left py-1.5 pr-2 font-medium">Maturity</th>
                    <th className="text-left py-1.5 pr-2 font-medium">On maturity</th>
                    <th className="text-left py-1.5 pr-2 font-medium">Destination</th>
                    <th className="py-1.5 font-medium w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {tds.map((td) => {
                    const matured = td.maturityDate && td.maturityDate < todayIso;
                    const daysSinceMat = matured
                      ? Math.floor(
                          (new Date(todayIso).getTime() - new Date(td.maturityDate).getTime()) /
                            (1000 * 60 * 60 * 24),
                        )
                      : 0;
                    return (
                      <tr key={td.id} className="border-b border-edge/50">
                        <td className="py-1 pr-2">
                          <input
                            type="text"
                            className="input w-24"
                            placeholder="BNZ"
                            value={td.bank}
                            disabled={isViewer}
                            onChange={(e) => updateTd(td.id, { bank: e.target.value })}
                          />
                        </td>
                        <td className="py-1 pr-2">
                          <input
                            type="text"
                            className="input w-32"
                            placeholder="TD-12345"
                            value={td.reference}
                            disabled={isViewer}
                            onChange={(e) => updateTd(td.id, { reference: e.target.value })}
                          />
                        </td>
                        <td className="py-1 pr-2">
                          <input
                            type="number"
                            step="0.01"
                            className="input num w-28 text-right"
                            value={td.principal}
                            disabled={isViewer}
                            onChange={(e) =>
                              updateTd(td.id, { principal: parseFloat(e.target.value) || 0 })
                            }
                          />
                        </td>
                        <td className="py-1 pr-2">
                          <input
                            type="number"
                            step="0.01"
                            className="input num w-20 text-right"
                            value={td.rateAnnual}
                            disabled={isViewer}
                            onChange={(e) =>
                              updateTd(td.id, { rateAnnual: parseFloat(e.target.value) || 0 })
                            }
                          />
                        </td>
                        <td className="py-1 pr-2">
                          <input
                            type="date"
                            className="input"
                            value={td.startDate}
                            disabled={isViewer}
                            onChange={(e) => updateTd(td.id, { startDate: e.target.value })}
                          />
                        </td>
                        <td className="py-1 pr-2">
                          <div className="flex flex-col gap-1">
                            <input
                              type="date"
                              className="input"
                              value={td.maturityDate}
                              disabled={isViewer}
                              onChange={(e) => updateTd(td.id, { maturityDate: e.target.value })}
                            />
                            {matured && (
                              <span
                                className="text-[10px] font-semibold px-1.5 rounded bg-[var(--amber-lt)] text-[var(--amber)] self-start"
                                title={
                                  td.rolloverPolicy === 'auto-rollover'
                                    ? 'Auto-rollover TD past its maturity date — update start/maturity/rate in place once the bank confirms new terms.'
                                    : 'TD past maturity — once proceeds land in destination account, delete this row.'
                                }
                              >
                                matured {daysSinceMat}d ago — update terms?
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-1 pr-2">
                          <select
                            className="input"
                            value={td.rolloverPolicy}
                            disabled={isViewer}
                            onChange={(e) =>
                              updateTd(td.id, {
                                rolloverPolicy: e.target.value as 'auto-rollover' | 'to-account',
                              })
                            }
                          >
                            <option value="auto-rollover">Auto-rollover</option>
                            <option value="to-account">Pay to account</option>
                          </select>
                        </td>
                        <td className="py-1 pr-2">
                          {td.rolloverPolicy === 'to-account' ? (
                            <select
                              className="input"
                              value={td.destinationAccountId ?? ''}
                              disabled={isViewer}
                              onChange={(e) =>
                                updateTd(td.id, {
                                  destinationAccountId: e.target.value || null,
                                })
                              }
                            >
                              <option value="">— select —</option>
                              {accounts.map((a) => (
                                <option key={a.id} value={a.id}>
                                  {a.name || '(unnamed)'}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="text-[11px] text-ccaanz-grey">Stays as TD</span>
                          )}
                        </td>
                        <td className="py-1">
                          {!isViewer && (
                            <button
                              type="button"
                              onClick={() => removeTd(td.id)}
                              className="text-ccaanz-grey hover:text-ccaanz-red"
                              title="Delete this TD"
                              aria-label="Delete TD"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            {!isViewer && (
              <div className="mt-2">
                <button type="button" onClick={addTd} className="btn btn-ghost btn-sm">
                  <Plus className="w-3.5 h-3.5" />
                  Add Term Deposit
                </button>
              </div>
            )}
          </section>

          {err && <div className="text-[12px] text-ccaanz-red mt-2">{err}</div>}
        </div>

        <div className="panel-section border-t border-edge flex items-center justify-between gap-2 shrink-0">
          <div className="text-[12px] text-ccaanz-grey">
            {accounts.length} account{accounts.length === 1 ? '' : 's'} · {tds.length} TD
            {tds.length === 1 ? '' : 's'}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="btn btn-ghost btn-sm">
              Cancel
            </button>
            {!isViewer && (
              <button
                type="button"
                onClick={handleSave}
                disabled={busy}
                className="btn btn-primary btn-sm"
              >
                <Save className="w-3.5 h-3.5" />
                {busy ? 'Saving…' : 'Save'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
